"use client"

import { ApolloProvider } from "@apollo/client"
import { ChakraProvider } from "@chakra-ui/react"
import React, { type PropsWithChildren } from "react"

import { Custom404Page } from "components/Custom404Page"
import { ExperimentProvider } from "components/ExperimentProvider"
import { FeatureFlagProvider } from "components/FeatureFlagProvider"
import { UserProvider } from "components/UserProvider"
import { GTM } from "components/GTM"
import {
  DefaultLayout,
  BlogLayout,
  QuizLayout,
  QuizLayoutV2,
} from "components/SiteChrome/Layouts"
import { OneTrustCookieBanner } from "components/OneTrustCookieBanner"
import { TrustPilot } from "components/TrustPilot"
import { useOnChangeStore, useSetReferrer } from "hooks"
import { usePageSet } from "hooks/usePageSet"
import { useStoreAwinId } from "hooks/useStoreAwinId"
import type { ContextHeaderType } from "lib/apollo/types"
import { getClient } from "lib/apolloClient"
import { ErrorBoundary } from "react-error-boundary"
import { theme } from "theme"
import "theme/adyen.legacy.css"
import "theme/animations.legacy.css"
import "theme/loqate.legacy.css"
import "theme/newsletterpopupoverride.legacy.css"
import { Segment } from "tracking/components"
import { useSetLaunchDarklyId } from "tracking/hooks/useSetLaunchDarklyId"
import { initDatadogLogs } from "utils/datadog"
import { registerServiceWorker } from "utils/serviceWorker"
import { initUserSegment } from "utils/userSegment"
import "../polyfills"
import { reportAccessibility } from "utils/reportAccessibility"
import { SpeedCurveABTestTracking } from "components/SpeedCurveABTestTracking"
import { ATBConfirmationProvider } from "basket_and_checkout/shared/components/ATBConfirmation/context/ATBConfirmationContext"
import { PromoCodeABTestTracking } from "components/PromoCodeABTestTracking"
import { USJourneyABTestTracking } from "components/USJourneyABTestTracking"
import type { GetQuizDataProps } from "lib/quiz/types"

import { AppContextProvider } from "../utils/app-context"
import { getTrackingEventId } from "lib/api/utils/trackingEventId"
import { DigiohLightboxScript } from "./scripts/DigiohLightboxScript"

initDatadogLogs()
initUserSegment()

type UserType = "lapsedmember" | "member" | "non-member"

type PageProps = {
  layout?: "QuizLayout" | "QuizLayoutV2" | "BlogLayout" | "DefaultLayout" | null
  layoutProps?: Record<string, unknown> // or define more specifically for each layout
  data?: GetQuizDataProps // Add this when the layout is QuizLayout
  variant?: {
    availability?: {
      status?: string
    }
  }
  userType?: UserType
  productIds?: string[]
  experimentFlags?: Record<string, number | string | boolean>
}

type Props = {
  headers: ContextHeaderType
  children: React.ReactNode
  pageProps: PageProps
  renderSiteChrome?: boolean
}

type LayoutProps = PropsWithChildren & {
  pageProps: PageProps
}

const Layout: React.FC<LayoutProps> = ({ children, pageProps }) => {
  switch (pageProps.layout) {
    case null:
      return <>{children}</>
    case "QuizLayout":
      return (
        <QuizLayout
          showTrustPilotReviewsWidget
          {...pageProps?.layoutProps}
          data={pageProps.data as GetQuizDataProps}
        >
          {children}
        </QuizLayout>
      )
    case "QuizLayoutV2":
      return <QuizLayoutV2 {...pageProps?.layoutProps}>{children}</QuizLayoutV2>
    case "BlogLayout":
      return <BlogLayout {...pageProps?.layoutProps}>{children}</BlogLayout>
    default:
      return (
        <DefaultLayout {...pageProps?.layoutProps}>{children}</DefaultLayout>
      )
  }
}

// https://reactjs.org/link/uselayouteffect-ssr
if (typeof window === "undefined") React.useLayoutEffect = React.useEffect

// eslint-disable-next-line no-restricted-syntax
export default function App({
  headers,
  children,
  pageProps,
}: Props): JSX.Element {
  registerServiceWorker()
  useSetReferrer()
  usePageSet()
  useOnChangeStore()
  useSetLaunchDarklyId()
  useStoreAwinId()
  const eventId = getTrackingEventId()

  const isOos = pageProps?.variant?.availability?.status
    ? pageProps?.variant?.availability.status === "outofstock"
    : undefined

  const productIds = pageProps?.productIds ?? []
  return (
    <ChakraProvider resetCSS theme={theme}>
      <ErrorBoundary FallbackComponent={() => <Custom404Page />}>
        <ApolloProvider client={getClient()}>
          <AppContextProvider headers={headers}>
            <UserProvider type={pageProps?.userType ?? "non-member"}>
              <ExperimentProvider flags={pageProps?.experimentFlags ?? {}}>
                <ATBConfirmationProvider>
                  <FeatureFlagProvider>
                    <SpeedCurveABTestTracking />
                    <PromoCodeABTestTracking />
                    <USJourneyABTestTracking />
                    <OneTrustCookieBanner />
                    <GTM is404={false} eventId={eventId} />
                    <TrustPilot />
                    <Segment
                      isOos={isOos}
                      productIds={productIds}
                      eventId={eventId}
                    />
                    <DigiohLightboxScript />
                    <Layout pageProps={pageProps}>{children}</Layout>
                  </FeatureFlagProvider>
                </ATBConfirmationProvider>
              </ExperimentProvider>
            </UserProvider>
          </AppContextProvider>
        </ApolloProvider>
      </ErrorBoundary>
    </ChakraProvider>
  )
}
reportAccessibility(React)
